import React, { useEffect, useRef, useState } from "react";
import { GoogleMap, TrafficLayer, useJsApiLoader } from "@react-google-maps/api";
import { Box, Button } from "@mui/material";
import mapStyles from "../../assets/estilos/mapStyles";
import ClusterManager from "./ClusterManager";

const MostrarMapa = ({ vehiculos, centro, seguimientoActivo, onDesactivarSeguimiento }) => {

  const mapRef = useRef(null);
  const markersRef = useRef({});
  const ultimaActualizacionRef = useRef({});
  const seguimientoMarkerRef = useRef(null);
  const animationRefs = useRef({});
  const markerStatesRef = useRef({});
  const [zoomActual, setZoomActual] = useState(11);
  
  const [mapReady, setMapReady] = useState(false);
  const [marcadoresListos, setMarcadoresListos] = useState(0);
  const [mapTypeId, setMapTypeId] = useState("roadmap");

  // Bandera para asegurar que el zoom automático de todos los vehículos solo ocurra al inicio/carga
  const initialFitDone = useRef(false);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY
  });

  // Ajuste de zoom y centrado según vehículos o seguimiento activo
  useEffect(() => {
    if (!mapRef.current || !mapReady || !vehiculos || vehiculos.length === 0) return;

    const google = window.google;

    // Si hay seguimiento activo de un vehículo específico
    if (seguimientoActivo && centro) {
      mapRef.current.panTo(centro);
      mapRef.current.setZoom(17);
      return;
    }

    // Si aún no se ha hecho el ajuste inicial para abarcar a todos los vehículos
    if (!initialFitDone.current) {
      const bounds = new google.maps.LatLngBounds();
      let hasValidCoords = false;

      vehiculos.forEach((v) => {
        const lat = Number(v.latitude);
        const lng = Number(v.longitude);
        if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
          bounds.extend({ lat, lng });
          hasValidCoords = true;
        }
      });

      if (hasValidCoords) {
        // Si solo hay 1 vehículo, fitBounds puede dejar el zoom demasiado cerca (ej. zoom 21), 
        // por lo que limitamos o manejamos un caso base si es necesario.
        mapRef.current.fitBounds(bounds);
        
        // Opcional: si hay un solo vehículo, ajustamos un zoom por defecto razonable (ej. 14)
        if (vehiculos.length === 1) {
          const singleLat = Number(vehiculos[0].latitude);
          const singleLng = Number(vehiculos[0].longitude);
          mapRef.current.setCenter({ lat: singleLat, lng: singleLng });
          mapRef.current.setZoom(14);
        }

        initialFitDone.current = true; // Evita que re-centre cada vez que la posición del vehículo se actualice en tiempo real
      }
    }
  }, [vehiculos, centro, seguimientoActivo, mapReady]);

  const animateMarker = (marker, markerId, newPosition) => {
    const now = Date.now();

    if (!markerStatesRef.current[markerId]) {
      const currentPos = marker.getPosition();
      markerStatesRef.current[markerId] = {
        animationFrame: null,
        currentPosition: currentPos
          ? { lat: currentPos.lat(), lng: currentPos.lng() }
          : newPosition,
        targetPosition: newPosition,
        startPosition: newPosition,
        startTime: now,
        duration: 10000,
        lastUpdateTime: now - 10000
      };
    }

    const state = markerStatesRef.current[markerId];

    if (state.animationFrame) {
      cancelAnimationFrame(state.animationFrame);
    }

    const currentVisualPosition = state.currentPosition;
    let calculatedDuration = now - state.lastUpdateTime;

    if (calculatedDuration < 2000 || calculatedDuration > 25000) {
      calculatedDuration = 10000; 
    }

    state.lastUpdateTime = now;
    state.startPosition = { lat: currentVisualPosition.lat, lng: currentVisualPosition.lng };
    state.targetPosition = { lat: Number(newPosition.lat), lng: Number(newPosition.lng) };
    state.startTime = now;
    state.duration = calculatedDuration; 

    const animate = () => {
      const elapsed = Date.now() - state.startTime;
      let progress = elapsed / state.duration;

      if (progress > 1) progress = 1;

      const lat = state.startPosition.lat + (state.targetPosition.lat - state.startPosition.lat) * progress;
      const lng = state.startPosition.lng + (state.targetPosition.lng - state.startPosition.lng) * progress;

      const nextPosition = { lat, lng };
      state.currentPosition = nextPosition;
      marker.setPosition(nextPosition);

      if (seguimientoActivo && seguimientoMarkerRef.current === marker && mapRef.current) {
        mapRef.current.moveCamera({ center: nextPosition });
      }

      if (progress < 1) {
        state.animationFrame = requestAnimationFrame(animate);
      }
    };

    animate();
  };

  /* 🚗 MARKERS - Creación de marcadores y gatillo de sincronización */
  useEffect(() => {
    if (!isLoaded || !mapRef.current || !mapReady) return;

    const google = window.google;
    const nuevosIds = {};

    vehiculos.forEach((v) => {
      if (!v.latitude || !v.longitude) return;

      const id = v.idVehiculo;
      nuevosIds[id] = true;

      if (markersRef.current[id]) {
        animateMarker(markersRef.current[id], id, {
          lat: Number(v.latitude),
          lng: Number(v.longitude),
        });
      } else {
        const marker = new google.maps.Marker({
          position: {
            lat: Number(v.latitude),
            lng: Number(v.longitude),
          },
          title: `${v.modelo} - ${v.patente}`,
          icon: {
            url: v.imagen,
            scaledSize: new google.maps.Size(45, 45),
          },
        });

        markersRef.current[id] = marker;
      }

      if (
        seguimientoActivo &&
        centro &&
        Math.abs(v.latitude - centro.lat) < 0.0001 &&
        Math.abs(v.longitude - centro.lng) < 0.0001
      ) {
        seguimientoMarkerRef.current = markersRef.current[id];
      }
    });

    Object.keys(markersRef.current).forEach((id) => {
      if (!nuevosIds[id]) {
        markersRef.current[id].setMap(null);
        delete markersRef.current[id];
        delete ultimaActualizacionRef.current[id];

        if (markerStatesRef.current[id]) {
          if (markerStatesRef.current[id].animationFrame) {
            cancelAnimationFrame(markerStatesRef.current[id].animationFrame);
          }
          delete markerStatesRef.current[id];
        }
      }
    });

    setMarcadoresListos(Date.now());

  }, [vehiculos, isLoaded, mapReady, seguimientoActivo, centro]);

  if (!isLoaded) return null;

  return (
    <Box sx={{ width: "100%", height: "100%", position: "relative" }}>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        zoom={11}
        center={centro || { lat: -33.4489, lng: -70.6693 }}
        mapTypeId={mapTypeId}
        options={{
          streetViewControl: false,
          fullscreenControl: false,
          mapTypeControl: false,
          zoomControl: false,
          rotateControl: false,
          styles: mapStyles,
          draggable: true,
          gestureHandling: "greedy",
        }}
        onZoomChanged={() => {
          if (!mapRef.current) return;
          const zoom = Math.round(mapRef.current.getZoom());
          setZoomActual((prev) => (prev === zoom ? prev : zoom));
        }}
        onLoad={(map) => {
          mapRef.current = map;
          setMapReady(true);
        }}
        onDragStart={() => {
          if (seguimientoActivo && onDesactivarSeguimiento) {
            onDesactivarSeguimiento();
            seguimientoMarkerRef.current = null;
          }
        }}
      >
        <TrafficLayer />

        {mapReady && (
          <ClusterManager
            map={mapRef.current}
            vehiculos={vehiculos}
            zoomActual={zoomActual}
            markersRef={markersRef}
            seguimientoActivo={seguimientoActivo}
            seguimientoMarkerRef={seguimientoMarkerRef}
            marcadoresListos={marcadoresListos}
          />
        )}
      </GoogleMap>

      {/* BOTÓN ÚNICO Y MODERNO DE MAPA / SATÉLITE */}
      <Box sx={{ position: "absolute", top: 20, left: 20, zIndex: 10 }}>
        <Button
          onClick={() => setMapTypeId(prev => (prev === "roadmap" ? "satellite" : "roadmap"))}
          startIcon={
            mapTypeId === "satellite" ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon>
                <line x1="8" y1="2" x2="8" y2="18"></line>
                <line x1="16" y1="6" x2="16" y2="22"></line>
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M2 12h20"></path>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
              </svg>
            )
          }
          sx={{
            background: "#ffffff",
            color: "#1e293b",
            height: 44,
            px: 2.2,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            borderRadius: "24px",
            fontSize: "13px",
            fontWeight: 700,
            textTransform: "none",
            letterSpacing: "0.2px",
            border: "1px solid rgba(0,0,0,0.06)",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              background: "#f8fafc",
              boxShadow: "0 10px 28px rgba(0,0,0,0.18)",
              transform: "translateY(-1px)",
              color: "#2FA4B8"
            },
            "&:active": {
              transform: "translateY(0)"
            }
          }}
        >
          {mapTypeId === "satellite" ? "Vista Mapa" : "Vista Satélite"}
        </Button>
      </Box>
    </Box>
  );
};

export class DefaultMap {}
export default MostrarMapa;